import React from "react";
import RSelect from "react-select";

// ── TEK Select (AGENTS §8.1 — TEK SİSTEM) ──────────────────────────────────
// Tüm react-select kullanımının kanonik sarmalayıcısı. Tek `classNamePrefix`
// (`ds-select`), token-driven stil ve drawer'ın üstüne portal'lanan menü tek
// yerden gelir. Call-site'lar yalnızca `import Select from "react-select"`
// satırını `import { Select } from "@/design-system/components/Select"` ile
// değiştirir — value/onChange/options/isMulti/components/formatOptionLabel gibi
// tüm react-select prop'ları şeffaf geçer (`...rest`).
//
// İki görünüm (date/time picker ile aynı sözleşme — §5.4.1):
//   variant="boxed"  (DEFAULT) → standalone form/modal/filtre kutusu: görünür
//                                 --field-border + navy --field-border-focus halo.
//   variant="inline"           → Field satırı içinde akan BORDERSIZ kontrol
//                                 (çerçeveyi dış MetaFieldRow/Field sağlar).
//
// Renkler/radius/gölge TOKEN'dan (magic hex yok). Seçili option brand tonu
// color-mix ile üretilir.

const FOCUS_HALO = "0 0 0 3px color-mix(in srgb, var(--field-border-focus) 16%, transparent)";
const SELECTED_TINT = "color-mix(in srgb, var(--ds-brand) 20%, transparent)";

export function buildDsSelectStyles({
  variant = "boxed",
  compact = false,
  withAvatar = false,
} = {}) {
  const boxed = variant !== "inline";
  return {
    control: (base, state) => ({
      ...base,
      minHeight: compact ? 34 : 38,
      borderRadius: "var(--rad-md)",
      borderColor: boxed
        ? state.isFocused
          ? "var(--field-border-focus)"
          : "var(--field-border)"
        : state.isFocused
          ? "var(--field-border)"
          : "transparent",
      backgroundColor: boxed
        ? "var(--field-bg)"
        : state.isFocused
          ? "var(--field-bg)"
          : "transparent",
      boxShadow: state.isFocused ? FOCUS_HALO : "none",
      transition:
        "background-color 0.16s ease, border-color 0.16s ease, box-shadow 0.16s ease",
      "&:hover": {
        borderColor: boxed ? "var(--field-border-hover)" : "var(--field-border)",
        backgroundColor: "var(--field-bg)",
      },
      ...(withAvatar ? { alignItems: "center" } : null),
    }),
    valueContainer: (base) => ({
      ...base,
      padding: compact ? "1px 10px" : "2px 11px",
      gap: 6,
      flexWrap: withAvatar ? "nowrap" : base.flexWrap,
      overflowX: withAvatar ? "auto" : base.overflowX,
      overflowY: "hidden",
      scrollbarWidth: "none",
    }),
    input: (base) => ({ ...base, margin: 0, padding: 0, color: "var(--ds-text)" }),
    placeholder: (base) => ({
      ...base,
      color: "var(--ds-text-muted)",
      fontSize: compact ? 12 : 13,
    }),
    singleValue: (base) => ({
      ...base,
      color: "var(--ds-text)",
      fontSize: compact ? 12.5 : 13.5,
      fontWeight: 500,
    }),
    multiValue: (base, props) => ({
      ...base,
      borderRadius: "var(--rad-pill)",
      border: "1px solid var(--ds-border)",
      backgroundColor: props?.data?.color
        ? `${props.data.color}1A`
        : "var(--ds-surface)",
      color: props?.data?.color || "var(--ds-text-secondary)",
      alignItems: "center",
      maxWidth: "100%",
    }),
    multiValueLabel: (base, props) => ({
      ...base,
      color: props?.data?.color || "var(--ds-text-secondary)",
      fontSize: 11,
      fontWeight: 600,
      paddingInline: 8,
    }),
    multiValueRemove: (base, props) => ({
      ...base,
      color: props?.data?.color || "var(--ds-text-muted)",
      borderRadius: "var(--rad-pill)",
      ":hover": {
        backgroundColor: "color-mix(in srgb, var(--ds-bg) 60%, transparent)",
        color: props?.data?.color || "var(--ds-text)",
      },
    }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base, state) => ({
      ...base,
      color: state.isFocused ? "var(--ds-text-secondary)" : "var(--ds-text-muted)",
      padding: "0 10px 0 4px",
    }),
    clearIndicator: (base) => ({
      ...base,
      color: "var(--ds-text-muted)",
      padding: "0 6px",
    }),
    // Drawer (.ds-modal) z-index --z-modal(1800) → menü body'ye portal'lanınca
    // ARKADA kalmasın diye z-scale'in en üstü (--z-toast 1900) ile öne çıkar.
    menuPortal: (base) => ({ ...base, zIndex: "var(--z-toast)" }),
    menu: (base) => ({
      ...base,
      marginTop: 8,
      border: "1px solid var(--ds-border)",
      borderRadius: "var(--rad-lg)",
      overflow: "hidden",
      boxShadow: "var(--ds-shadow-3)",
    }),
    menuList: (base) => ({ ...base, padding: 6 }),
    option: (base, state) => ({
      ...base,
      borderRadius: "var(--rad-md)",
      backgroundColor: state.isSelected
        ? SELECTED_TINT
        : state.isFocused
          ? "var(--ds-surface)"
          : "transparent",
      color: "var(--ds-text)",
      cursor: "pointer",
      padding: "9px 11px",
      fontSize: 12.5,
      fontWeight: state.isSelected ? 600 : 500,
    }),
  };
}

const BODY = typeof document !== "undefined" ? document.body : null;

// Kanonik Select. react-select'i şeffaf sarar; yalnızca prefix/stil/portal
// varsayılanlarını enjekte eder. Çağıran kendi `styles`/`classNamePrefix`/
// `menuPortalTarget`'ını verirse o kazanır (geri uyumluluk).
export function Select({
  variant = "boxed",
  compact = false,
  withAvatar = false,
  styles,
  classNamePrefix = "ds-select",
  menuPortalTarget = BODY,
  menuPosition,
  ...rest
}) {
  const resolvedStyles =
    styles || buildDsSelectStyles({ variant, compact, withAvatar });
  return (
    <RSelect
      classNamePrefix={classNamePrefix}
      styles={resolvedStyles}
      menuPortalTarget={menuPortalTarget}
      menuPosition={menuPosition || (menuPortalTarget ? "fixed" : "absolute")}
      {...rest}
    />
  );
}

export default Select;
